import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { useState } from 'react'
import { MainPane, Header, Headline, Contents } from 'pages/layout/TwoColumn'
import { query } from 'config/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isProdOrBeta } from 'config/environment'
import { AdminLoadTestSection } from './AdminLoadTestSection'

export function AdminServerTestPage() {
  const [jobId, setJobId] = useState<string | null>(null)

  const apiCall = useQuery({
    ...query.api.v1.test.index.queryOptions(),
    enabled: false,
  })

  const schedule = useMutation(query.api.v1.test.job.store.mutationOptions())
  const causeError = useMutation(query.api.v1.test.error.mutationOptions())

  const jobQuery = useQuery({
    ...query.api.v1.test.job.show.queryOptions({ params: { jobId: jobId! } }),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || 'error' in data) return false
      if (data.state === 'completed' || data.state === 'failed') return false
      return 1000
    },
  })

  const jobData =
    jobQuery.data && !('error' in jobQuery.data) ? jobQuery.data : null

  return (
    <MainPane>
      <Header>
        <Headline>Server Test</Headline>
      </Header>
      <Contents>
        <div className="flex flex-col gap-4 p-6">
          <BreakoutButton
            onClick={() => apiCall.refetch()}
            disabled={apiCall.isFetching}
            kind="secondary"
            size="large"
          >
            {apiCall.isFetching ? 'Calling…' : 'API Call Test'}
          </BreakoutButton>

          {apiCall.data && (
            <div className="rounded-3xl bg-surface p-6">
              <div className="text-body-medium text-on-surface-var">
                API response number
              </div>
              <div className="mt-1 text-headline-large">
                {apiCall.data.number}
              </div>
            </div>
          )}

          {apiCall.isError && (
            <div className="text-body-medium text-core-error">
              Error: {apiCall.error?.message}
            </div>
          )}

          <BreakoutButton
            onClick={() =>
              schedule.mutate(
                { body: {} },
                {
                  onSuccess: (data) => {
                    if (!data.jobId) return
                    setJobId(data.jobId)
                  },
                }
              )
            }
            disabled={schedule.isPending}
            kind="accent"
            size="large"
          >
            {schedule.isPending ? 'Scheduling…' : 'Schedule Test Job'}
          </BreakoutButton>

          <BreakoutButton
            onClick={() =>
              schedule.mutate(
                { body: { crash: true } },
                {
                  onSuccess: (data) => {
                    if (!data.jobId) return
                    setJobId(data.jobId)
                  },
                }
              )
            }
            disabled={schedule.isPending}
            kind="error"
            size="large"
          >
            {schedule.isPending
              ? 'Scheduling…'
              : 'Schedule Crashing Job (Sentry worker test)'}
          </BreakoutButton>

          {jobId && (
            <div className="text-body-medium text-on-surface-var">
              Job ID: <code>{jobId}</code>
            </div>
          )}

          {jobData &&
            jobData.state !== 'completed' &&
            jobData.state !== 'failed' && (
              <div className="text-body-medium text-on-surface-var">
                Status: {jobData.state}…
              </div>
            )}

          {jobData?.state === 'failed' && (
            <div className="text-body-medium text-core-error">Job failed.</div>
          )}

          {jobData?.state === 'completed' && jobData.result !== null && (
            <div className="rounded-3xl bg-surface p-6">
              <div className="text-body-medium text-on-surface-var">
                Generated number
              </div>
              <div className="mt-1 text-headline-large">
                {jobData.result?.number}
              </div>
            </div>
          )}

          {(schedule.isError || jobQuery.isError) && (
            <div className="text-body-medium text-core-error">
              Error: {schedule.error?.message ?? jobQuery.error?.message}
            </div>
          )}

          {!isProdOrBeta() && <AdminLoadTestSection />}

          <BreakoutButton
            onClick={() => causeError.mutate({})}
            disabled={causeError.isPending}
            kind="error"
            size="large"
          >
            {causeError.isPending
              ? 'Causing error…'
              : 'Cause Error (Sentry test)'}
          </BreakoutButton>

          {causeError.isError && (
            <div className="text-body-medium text-core-error">
              Error sent to Sentry: {causeError.error?.message}
            </div>
          )}
        </div>
      </Contents>
    </MainPane>
  )
}
